import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const ABOUT_SECTION_ID = "about-heading";

const MEGA_EVENT = "vh-services-mega-open";

/**
 * Whether the About section is the "current" scroll region for the nav highlight.
 * Uses getBoundingClientRect + scroll/resize (not IntersectionObserver alone) so
 * leaving the section always clears the state.
 */
function computeAboutSectionInView() {
  const el = document.getElementById(ABOUT_SECTION_ID);
  if (!el) return false;

  const r = el.getBoundingClientRect();
  const vh = window.innerHeight;
  const header = 72;

  // Scrolled past: section entirely above the viewport (footer, contact, etc.)
  if (r.bottom <= header + 16) return false;

  // Hero / top: section still below the fold
  if (r.top >= vh * 0.82) return false;

  // Mostly scrolled past: only a sliver of the section remains at the top edge
  if (r.height > 0 && r.top < -r.height * 0.5) return false;

  // Section pushed too far down — user is reading content below About's anchor
  if (r.top > vh * 0.36) return false;

  return true;
}

/**
 * True when the landing About section is visibly in view (home only).
 * Suppressed while Services mega is open so both nav items never read "active".
 */
export function useAboutSectionNavActive() {
  const { pathname } = useLocation();
  const [sectionInView, setSectionInView] = useState(false);
  const [servicesMegaOpen, setServicesMegaOpen] = useState(false);

  useEffect(() => {
    const onMega = (e) => {
      setServicesMegaOpen(Boolean(e.detail?.open));
    };
    const syncFromDom = () => {
      if (typeof document === "undefined") return;
      const mega = document.querySelector(
        ".header__menu--new > li.services.vh-nav-services--mega-open"
      );
      setServicesMegaOpen(Boolean(mega));
    };
    window.addEventListener(MEGA_EVENT, onMega);
    syncFromDom();
    requestAnimationFrame(syncFromDom);
    return () => window.removeEventListener(MEGA_EVENT, onMega);
  }, []);

  useEffect(() => {
    if (pathname !== "/") {
      setSectionInView(false);
      return undefined;
    }

    let ticking = false;
    const update = () => {
      setSectionInView(computeAboutSectionInView());
    };

    const onScrollOrResize = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        update();
      });
    };

    update();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [pathname]);

  return pathname === "/" && sectionInView && !servicesMegaOpen;
}

export function scrollToAboutSection() {
  const el = document.getElementById(ABOUT_SECTION_ID);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}
